/* =====================================================================
 * @auw/onprem — module-scope TenantManifest snapshot.
 *
 * Runtime loaders at `askyourai/src/monitoring/*` and
 * `askyourai/src/lib/*` are plain modules invoked outside React —
 * `useTenant()` is unavailable there. To let them make tenant-aware
 * decisions without round-tripping through every call site, the app
 * shell publishes the resolved `TenantManifest` here at bootstrap via
 * `setTenantSnapshot()`, and the non-React loaders read it back with
 * `getTenantSnapshot()`.
 *
 * Design notes
 *   - Snapshot writes are idempotent-by-identity: calling
 *     `setTenantSnapshot()` with the same reference is a no-op. If a
 *     *different* object is published, we replace and emit a warning
 *     in development because tenant manifests should be treated as
 *     immutable per application session.
 *   - Reads always return the raw reference (no defensive copy). The
 *     `TenantManifest` shape is `readonly` at the type level, so
 *     consumers are expected not to mutate.
 *   - The module intentionally avoids importing `react` so it remains
 *     usable from workers, service workers, node scripts, and tests.
 * ===================================================================== */

import type { TenantManifest } from '@auw/tenant';

let current: TenantManifest | null = null;

/**
 * Publish the resolved tenant so module-scope loaders can gate on it.
 * Call this exactly once, as early as possible, from `main.tsx`
 * (before `<TenantProvider>` mounts).
 */
export function setTenantSnapshot(tenant: TenantManifest): void {
  if (current === tenant) return;
  if (current !== null && current.id !== tenant.id) {
    // eslint-disable-next-line no-console
    console.warn(
      `[@auw/onprem] tenant snapshot replaced mid-session: "${current.id}" -> "${tenant.id}". ` +
        'This is almost always a bug — module-scope gates may have latched on the old value.',
    );
  }
  current = tenant;
}

/**
 * Retrieve the published tenant, or `null` when `setTenantSnapshot`
 * has not yet run. Callers MUST handle the `null` case gracefully —
 * typically by falling back to the pre-Phase-8 behaviour (e.g. env
 * var only) so bootstrapping errors do not regress the SaaS path.
 */
export function getTenantSnapshot(): TenantManifest | null {
  return current;
}

/**
 * Test-only reset. Not exported from the package entry; internal
 * helpers that write unit tests against the snapshot can reach for it
 * via `@auw/onprem/src/tenantSnapshot` directly.
 */
export function __resetTenantSnapshotForTesting(): void {
  current = null;
}
